import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Shell from '../Shell';
import { api } from '../api';
import { useToast } from '../ToastContext';
import ThreadDrawer from '../components/ThreadDrawer';

const PROVIDER_ICON = { gmail: '📬', microsoft: '📧', imap: '🔌', simulated: '🧪' };

// ── Link-state chip ──────────────────────────────────────────────────────────
function LinkChip({ email }) {
  if (email.linkConfidence === 'High' && email.linkedEnvelopeId) {
    return (
      <span className="chip ready chip-sm">
        <span aria-hidden="true">✓</span> Auto-linked → {email.extractedQuotePreview?.rfqId ? String(email.extractedQuotePreview.rfqId).slice(-6) : 'RFQ'}
        {email.extractedQuotePreview?.revision != null ? ` (rev ${email.extractedQuotePreview.revision})` : ''}
      </span>
    );
  }
  if (email.linkConfidence === 'Medium' || email.suggestedEnvelopeId) {
    return <span className="chip review chip-sm"><span aria-hidden="true">◐</span> Suggested — confirm</span>;
  }
  if (email.linkConfidence === 'Manual' && email.linkedEnvelopeId) {
    return <span className="chip info chip-sm"><span aria-hidden="true">●</span> Manually linked</span>;
  }
  return <span className="chip muted chip-sm"><span aria-hidden="true">●</span> Unlinked</span>;
}

// ── Link-to-RFQ picker modal ─────────────────────────────────────────────────
function SearchableList({ items, getKey, getLabel, getSubtitle, value, onChange, placeholder, emptyHint, testid }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q) return items;
    const needle = q.toLowerCase();
    return items.filter((it) => {
      const label = (getLabel(it) || '').toLowerCase();
      const sub = (getSubtitle(it) || '').toLowerCase();
      return label.includes(needle) || sub.includes(needle);
    });
  }, [q, items, getLabel, getSubtitle]);

  return (
    <div data-testid={testid}>
      <input
        type="search"
        className="input"
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoFocus
        style={{ marginBottom: 8 }}
      />
      <div style={{
        maxHeight: 220, overflowY: 'auto',
        border: '1px solid var(--border-1)', borderRadius: 6,
        background: '#fff',
      }}>
        {items.length === 0 && (
          <div style={{ padding: 12, fontSize: 13, color: 'var(--ink-500)' }}>{emptyHint}</div>
        )}
        {items.length > 0 && filtered.length === 0 && (
          <div style={{ padding: 12, fontSize: 13, color: 'var(--ink-500)' }}>No matches for "{q}"</div>
        )}
        {filtered.map((it) => {
          const k = getKey(it);
          const isSelected = value === k;
          return (
            <div
              key={k}
              onClick={() => onChange(k)}
              style={{
                padding: '10px 12px',
                borderBottom: '1px solid var(--border-1)',
                cursor: 'pointer',
                background: isSelected ? 'var(--primary-tint)' : '#fff',
                borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-900)' }}>{getLabel(it)}</div>
              {getSubtitle(it) && (
                <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: 2 }}>{getSubtitle(it)}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LinkPickerModal({ email, onClose, onLinked }) {
  const [rfqs, setRfqs] = useState([]);
  const [rfqsLoaded, setRfqsLoaded] = useState(false);
  const [selectedRfqId, setSelectedRfqId] = useState('');
  const [envelopes, setEnvelopes] = useState([]);
  const [envelopesLoaded, setEnvelopesLoaded] = useState(false);
  const [selectedEnvId, setSelectedEnvId] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    api.listRFQs({ pageSize: 200 })
      .then((r) => { setRfqs(r.items || []); setRfqsLoaded(true); })
      .catch((e) => { setRfqsLoaded(true); toast.push('Failed to load RFQs: ' + e.message, { variant: 'error' }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedRfqId) { setEnvelopes([]); setEnvelopesLoaded(false); return; }
    setEnvelopesLoaded(false);
    api.listEnvelopes(selectedRfqId)
      .then((r) => { setEnvelopes(r.items || []); setEnvelopesLoaded(true); })
      .catch((e) => { setEnvelopesLoaded(true); toast.push('Failed to load envelopes: ' + e.message, { variant: 'error' }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRfqId]);

  async function confirm() {
    if (!selectedEnvId) { toast.push('Pick a supplier envelope first', { variant: 'error' }); return; }
    setBusy(true);
    try {
      const result = await api.linkEmail(email._id, { envelopeId: selectedEnvId });
      toast.push('Email linked successfully');
      onLinked(result.email || result);
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const selectedRfq = rfqs.find((r) => r._id === selectedRfqId);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        background: '#fff', borderRadius: 10, padding: 24, width: 560, maxHeight: '80vh',
        overflowY: 'auto', zIndex: 301,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Link to RFQ + Supplier</h2>
        <p style={{ fontSize: 12, color: 'var(--ink-500)', marginBottom: 16 }}>
          From: <b>{email.fromName || email.fromAddress}</b> · Subject: <span style={{ fontStyle: 'italic' }}>{email.subject || '(no subject)'}</span>
        </p>

        <div className="label" style={{ marginBottom: 4 }}>
          1. RFQ {!rfqsLoaded && <span style={{ color: 'var(--ink-500)' }}>(loading…)</span>}
          {rfqsLoaded && <span style={{ color: 'var(--ink-500)', fontWeight: 400 }}>· {rfqs.length} available</span>}
        </div>
        <SearchableList
          testid="link-rfq-search"
          items={rfqs}
          getKey={(r) => r._id}
          getLabel={(r) => r.rfqId || r._id}
          getSubtitle={(r) => [r.title, r.customer?.name, r.status].filter(Boolean).join(' · ')}
          value={selectedRfqId}
          onChange={(id) => { setSelectedRfqId(id); setSelectedEnvId(''); }}
          placeholder="Search by RFQ ID, title, customer, status…"
          emptyHint={rfqsLoaded ? 'No RFQs found in this tenant. Create one first, then come back to link.' : 'Loading…'}
        />

        {selectedRfqId && (
          <>
            <div className="label" style={{ marginTop: 16, marginBottom: 4 }}>
              2. Supplier envelope on <span className="mono" style={{ color: 'var(--primary)' }}>{selectedRfq?.rfqId}</span>
              {!envelopesLoaded && <span style={{ color: 'var(--ink-500)', fontWeight: 400 }}> · loading…</span>}
              {envelopesLoaded && <span style={{ color: 'var(--ink-500)', fontWeight: 400 }}> · {envelopes.length} dispatched</span>}
            </div>
            <SearchableList
              testid="link-envelope-search"
              items={envelopes}
              getKey={(e) => e._id}
              getLabel={(e) => e.supplier?.name || '(unknown supplier)'}
              getSubtitle={(e) => `Status: ${e.responseStatus}${e.coveragePct != null ? ` · ${e.coveragePct}% coverage` : ''}${e.sentAt ? ` · sent ${new Date(e.sentAt).toLocaleDateString()}` : ''}`}
              value={selectedEnvId}
              onChange={setSelectedEnvId}
              placeholder="Search supplier name…"
              emptyHint={envelopesLoaded ? 'No suppliers dispatched on this RFQ yet. Dispatch first, then return here.' : 'Loading…'}
            />
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn"
            onClick={confirm}
            disabled={busy || !selectedEnvId}
            data-testid="confirm-link-btn"
          >
            {busy ? 'Linking…' : 'Confirm link'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Suggest confirm modal ────────────────────────────────────────────────────
function SuggestConfirmModal({ email, onClose, onConfirmed }) {
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function confirm() {
    if (!email.suggestedEnvelopeId) return;
    setBusy(true);
    try {
      const result = await api.linkEmail(email._id, { envelopeId: email.suggestedEnvelopeId });
      toast.push('Link confirmed');
      onConfirmed(result.email);
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        background: '#fff', borderRadius: 10, padding: 24, width: 420, zIndex: 301,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>Confirm suggested link?</h2>
        <p style={{ fontSize: 13, color: 'var(--ink-500)', marginBottom: 16 }}>
          The system suggests linking this email to a supplier envelope. Confirm to accept.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={confirm} disabled={busy}>
            {busy ? 'Confirming…' : 'Confirm'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Main Mailbox page ────────────────────────────────────────────────────────
export default function Mailbox() {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState({ items: [], counts: {}, byConnector: [], hideSimulated: false });
  const [connectors, setConnectors] = useState([]);
  const [selected, setSelected] = useState(null);
  // Body is lazy-loaded — list endpoint omits it. We fetch on click and cache here.
  const [selectedBody, setSelectedBody] = useState({ loading: false, body: '', source: null, emailId: null });
  const [includeAll, setIncludeAll] = useState(false);
  const [page, setPage] = useState({ hasMore: false, nextCursor: null, retentionDays: 30 });
  const [loadingMore, setLoadingMore] = useState(false);
  const [localFilter, setLocalFilter] = useState('');
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [includeSim, setIncludeSim] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // D2-UI state
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [showSuggestConfirm, setShowSuggestConfirm] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);

  const toast = useToast();
  const nav = useNavigate();

  const statusFilter = sp.get('status') || '';
  const connectorId = sp.get('connectorId') || '';

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      const [mail, cust, conn] = await Promise.all([
        api.listInbound({
          status: statusFilter,
          connectorId,
          includeSimulated: includeSim ? '1' : undefined,
          includeAll: includeAll ? '1' : undefined,
          limit: 100,
        }),
        api.listCustomers(),
        api.listConnectors(),
      ]);
      setData(mail);
      setPage(mail.page || { hasMore: false, nextCursor: null, retentionDays: 30 });
      setCustomers(cust.items);
      setConnectors(conn.items || []);
    } catch (e) {
      setError(e.message || 'Failed to load mailbox');
      toast.push(e.message || 'Failed to load mailbox', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, [statusFilter, connectorId, includeSim, includeAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand the retention window: if the default (30-day) view is empty but
  // the tenant actually HAS emails (they're just older than the window), flip
  // includeAll so the user sees them without having to find a toggle. We only
  // do this once automatically — after that the user controls the checkbox.
  const [autoExpanded, setAutoExpanded] = useState(false);
  useEffect(() => {
    if (loading || includeAll || autoExpanded) return;
    const totalKnown = (data.counts.Unread || 0) + (data.counts.Read || 0)
      + (data.counts.Converted || 0) + (data.counts.Archived || 0);
    if (data.items.length === 0 && totalKnown > 0) {
      setAutoExpanded(true);
      setIncludeAll(true);
    }
  }, [loading, includeAll, autoExpanded, data.items.length, data.counts]);

  async function loadMore() {
    if (!page.hasMore || !page.nextCursor) return;
    setLoadingMore(true);
    try {
      const more = await api.listInbound({
        status: statusFilter,
        connectorId,
        includeSimulated: includeSim ? '1' : undefined,
        includeAll: includeAll ? '1' : undefined,
        limit: 100,
        cursor: page.nextCursor,
      });
      setData((d) => ({ ...d, items: [...d.items, ...(more.items || [])] }));
      setPage(more.page || { hasMore: false, nextCursor: null });
    } catch (e) {
      toast.push(e.message || 'Failed to load more', { variant: 'error' });
    } finally {
      setLoadingMore(false);
    }
  }

  const countsBy = useMemo(() => {
    const m = new Map();
    for (const b of data.byConnector || []) {
      const k = b.connectorId ? String(b.connectorId) : `__simulated__${b.source || ''}`;
      const existing = m.get(k) || { total: 0, unread: 0, source: b.source };
      existing.total += b.count || 0;
      existing.unread += b.unread || 0;
      m.set(k, existing);
    }
    return m;
  }, [data.byConnector]);

  async function markRead(email) {
    setSelected(email);
    if (email.status === 'Unread') {
      await api.patchInbound(email._id, { status: 'Read' });
      reload();
    }
  }

  // Lazy-load body whenever a different email is selected.
  // The list endpoint never ships the body, so we fetch it on click. Fast for
  // bodies cached in S3, slower (~200-1000 ms) when we go to the provider live.
  useEffect(() => {
    if (!selected?._id) {
      setSelectedBody({ loading: false, body: '', source: null, emailId: null });
      return;
    }
    if (selectedBody.emailId === String(selected._id)) return; // already loaded
    let cancelled = false;
    setSelectedBody({ loading: true, body: '', source: null, emailId: String(selected._id) });
    api.getInboundBody(selected._id)
      .then((r) => { if (!cancelled) setSelectedBody({ loading: false, body: r.body || '', source: r.source, emailId: String(selected._id) }); })
      .catch((e) => { if (!cancelled) setSelectedBody({ loading: false, body: `(failed to load body: ${e.message})`, source: 'error', emailId: String(selected._id) }); });
    return () => { cancelled = true; };
  }, [selected?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // `body` is the request body for POST /convert. The caller passes either
  // { customerId } to convert against an existing customer, or
  // { createCustomer: true, newCustomer } to create-and-convert atomically.
  async function convert(body) {
    if (!selected) return;
    // Back-compat: a bare customerId string still works.
    const payload = typeof body === 'string'
      ? (body ? { customerId: body } : {})
      : (body || {});
    setBusy(true);
    try {
      const res = await api.convertInbound(selected._id, payload);
      toast.push(`Converted → ${res.rfq.rfqId} · ${res.extractedLines} lines (${res.extractionSource})`);
      nav(`/rfq/${res.rfq._id}/structure`);
    } catch (e) { toast.push(e.message, { variant: 'error' }); }
    finally { setBusy(false); }
  }

  // Create a customer WITHOUT converting. Returns the new customer's id so the
  // ConvertControl can select it; the user then clicks Convert as a separate step.
  async function createCustomerOnly(newCustomer) {
    const body = {
      name: (newCustomer?.name || '').trim(),
      defaultCurrency: newCustomer?.defaultCurrency || 'USD',
    };
    if (!body.name) { toast.push('Customer name is required', { variant: 'error' }); return null; }
    // Carry the LLM/derived contact through as the primary contact.
    const contact = {};
    if (newCustomer?.contactName) contact.name = newCustomer.contactName;
    if (newCustomer?.contactEmail) contact.email = newCustomer.contactEmail;
    if (newCustomer?.contactPhone) contact.phone = newCustomer.contactPhone;
    if (Object.keys(contact).length) body.contacts = [contact];
    setBusy(true);
    try {
      const created = await api.createCustomer(body);
      const cust = await api.listCustomers();
      setCustomers(cust.items);
      toast.push(`Customer "${created.name}" created — review and convert`);
      return created._id;
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (!selected) return;
    await api.patchInbound(selected._id, { status: 'Archived' });
    setSelected(null);
    reload();
  }

  async function handleReextract() {
    if (!selected) return;
    setBusy(true);
    try {
      await api.reextractEmail(selected._id, { force: true });
      toast.push('Re-extraction triggered');
      reload();
    } catch (e) {
      toast.push(e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  function setConnectorFilter(id) {
    const p = new URLSearchParams(sp);
    if (id) p.set('connectorId', id); else p.delete('connectorId');
    setSp(p);
    setSelected(null);
  }

  function handleChipClick(e, email) {
    e.stopPropagation();
    if ((email.linkConfidence === 'Medium' || email.suggestedEnvelopeId) && !email.linkedEnvelopeId) {
      setSelected(email);
      setShowSuggestConfirm(true);
    }
  }

  function onLinked(updatedEmail) {
    // Update the selected email and the list in place
    setSelected(updatedEmail);
    setData((prev) => ({
      ...prev,
      items: prev.items.map((m) => m._id === updatedEmail._id ? updatedEmail : m),
    }));
    setShowLinkPicker(false);
    setShowSuggestConfirm(false);
  }

  // total unread across everything currently shown
  const totalUnread = data.counts.Unread || 0;
  const totalAll = (data.counts.Unread || 0) + (data.counts.Read || 0) + (data.counts.Converted || 0) + (data.counts.Archived || 0);
  const simulatedBucket = data.byConnector?.find((b) => b.source === 'Simulated');

  // threadId is checked in the detail pane to show "View thread" button

  return (
    <Shell
      title="Mailbox"
      subtitle={`${totalUnread} unread · ${data.counts.Converted || 0} converted · ${data.counts.Archived || 0} archived`}
    >
      <div className="mailbox-layout">
        {/* Account sidebar */}
        <aside className="mailbox-accounts">
          <div className="mailbox-account-head">Accounts</div>
          <div className={'mailbox-account ' + (!connectorId ? 'active' : '')} onClick={() => setConnectorFilter('')}>
            <div className="mailbox-account-ico">📥</div>
            <div className="mailbox-account-main">
              <div className="mailbox-account-title">All mailboxes</div>
              <div className="mailbox-account-meta">{totalAll} total</div>
            </div>
          </div>
          {connectors.map((c) => {
            const bucket = countsBy.get(String(c._id)) || { total: 0, unread: 0 };
            return (
              <div
                key={c._id}
                className={'mailbox-account ' + (connectorId === String(c._id) ? 'active' : '')}
                onClick={() => setConnectorFilter(String(c._id))}
              >
                <div className="mailbox-account-ico">{PROVIDER_ICON[c.provider] || '📨'}</div>
                <div className="mailbox-account-main">
                  <div className="mailbox-account-title">{c.label || c.displayEmail}</div>
                  <div className="mailbox-account-meta">
                    {c.displayEmail}
                    {bucket.unread > 0 && <span className="chip primary" style={{ marginLeft: 6 }}>{bucket.unread}</span>}
                  </div>
                </div>
                <div className="mailbox-account-count">{bucket.total}</div>
              </div>
            );
          })}
          {simulatedBucket && data.hideSimulated && (
            <label className="mailbox-account" style={{ cursor: 'pointer' }}>
              <div className="mailbox-account-ico">🧪</div>
              <div className="mailbox-account-main">
                <div className="mailbox-account-title">Simulated (hidden)</div>
                <div className="mailbox-account-meta">
                  <input type="checkbox" checked={includeSim} onChange={(e) => setIncludeSim(e.target.checked)} /> show seed
                </div>
              </div>
              <div className="mailbox-account-count">{simulatedBucket.count}</div>
            </label>
          )}
          {connectors.length === 0 && (
            <div className="mailbox-account" style={{ borderStyle: 'dashed', cursor: 'pointer' }} onClick={() => nav('/settings/email-inbox')}>
              <div className="mailbox-account-ico">➕</div>
              <div className="mailbox-account-main">
                <div className="mailbox-account-title">Connect a mailbox</div>
                <div className="mailbox-account-meta">Gmail, Outlook, IMAP</div>
              </div>
            </div>
          )}
        </aside>

        {/* Email list */}
        <div className="mailbox-list-col">
          <MailboxSearchBar
            connectors={connectors}
            selectedConnectorId={connectorId}
            localFilter={localFilter}
            setLocalFilter={setLocalFilter}
            onAfterRemoteSearch={() => reload()}
          />
          <div className="filter-bar" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
            {[['', 'All'], ['Unread', 'Unread'], ['Read', 'Read'], ['Converted', 'Converted'], ['Archived', 'Archived']].map(([v, label]) => (
              <span key={v} className={'chip ' + (statusFilter === v ? 'primary' : 'muted')} style={{ cursor: 'pointer' }}
                onClick={() => { const p = new URLSearchParams(sp); if (v) p.set('status', v); else p.delete('status'); setSp(p); }}>
                {label} {data.counts[v] ? `(${data.counts[v]})` : ''}
              </span>
            ))}
            <div style={{ flex: 1 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-500)' }} title="Hide emails older than the tenant retention window">
              <input type="checkbox" checked={includeAll} onChange={(e) => setIncludeAll(e.target.checked)} />
              Show all (older than {page.retentionDays || 30}d)
            </label>
          </div>
          <div className="mailbox-list">
            {(() => {
              // Loading: show skeleton rows so the panel doesn't false-empty flash.
              if (loading && data.items.length === 0) {
                return (
                  <div style={{ padding: 8 }}>
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="mail-item" style={{ pointerEvents: 'none' }}>
                        <div className="mail-from" style={{ background: 'var(--surface-2)', color: 'transparent', borderRadius: 4, width: '40%' }}>—</div>
                        <div className="mail-subject" style={{ background: 'var(--surface-2)', color: 'transparent', borderRadius: 4, width: '70%', marginTop: 4 }}>—</div>
                        <div className="mail-snippet" style={{ background: 'var(--surface-1)', color: 'transparent', borderRadius: 4, width: '55%', marginTop: 4 }}>—</div>
                      </div>
                    ))}
                  </div>
                );
              }
              // Error: surface a retryable banner instead of a blank pane.
              if (error && data.items.length === 0) {
                return (
                  <div style={{ padding: 12 }}>
                    <div className="banner risk">
                      <div style={{ flex: 1 }}>Couldn’t load your mailbox. {error}</div>
                      <button className="btn btn-sm btn-secondary" onClick={reload}>Retry</button>
                    </div>
                  </div>
                );
              }
              const needle = localFilter.trim().toLowerCase();
              // Body is no longer in the listing — local filter only sees what
              // the API returns. For body search the user should use the per-
              // connector "Search Gmail" button which queries the provider live.
              const filteredItems = needle
                ? data.items.filter((m) =>
                    [m.subject, m.fromName, m.fromAddress]
                      .filter(Boolean)
                      .some((s) => String(s).toLowerCase().includes(needle))
                  )
                : data.items;
              if (data.items.length === 0) {
                // Counts ignore the retention window, but the list applies it.
                // If the tenant HAS emails (counts > 0) yet none fall inside the
                // window, the list is empty only because everything is older
                // than the cutoff — surface that explicitly with a one-click fix
                // instead of a generic "Nothing here" that reads as broken.
                const totalKnown = (data.counts.Unread || 0) + (data.counts.Read || 0)
                  + (data.counts.Converted || 0) + (data.counts.Archived || 0);
                if (totalKnown > 0 && !includeAll) {
                  return (
                    <div className="empty">
                      <h3>Nothing in the last {page.retentionDays || 30} days</h3>
                      <p>
                        {totalKnown} email{totalKnown === 1 ? '' : 's'} {totalKnown === 1 ? 'is' : 'are'} older than the
                        {' '}{page.retentionDays || 30}-day window and {totalKnown === 1 ? "isn't" : "aren't"} shown by default.
                      </p>
                      <button className="btn btn-sm" onClick={() => setIncludeAll(true)}>
                        Show all {totalKnown} email{totalKnown === 1 ? '' : 's'}
                      </button>
                    </div>
                  );
                }
                return (
                  <div className="empty">
                    <h3>Nothing here</h3>
                    <p>{connectorId ? 'No emails match this filter.' : 'Connect a mailbox to start ingesting real emails.'}</p>
                    {!connectorId && <button className="btn btn-sm" onClick={() => nav('/settings/email-inbox')}>+ Connect mailbox</button>}
                  </div>
                );
              }
              if (filteredItems.length === 0) {
                return (
                  <div className="empty">
                    <h3>No matches for "{localFilter}"</h3>
                    <p>Try a different search, clear the filter, or use "Search Gmail" to pull older messages from your provider.</p>
                  </div>
                );
              }
              const rendered = filteredItems.map((m) => (
                <div
                  key={m._id}
                  className={'mail-item ' + (selected?._id === m._id ? 'selected' : '') + (m.status === 'Unread' ? ' unread' : '')}
                  onClick={() => markRead(m)}
                >
                  <div className="mail-from">{m.fromName || m.fromAddress}</div>
                  <div className="mail-subject">{m.subject || '(no subject)'}</div>
                  <div className="mail-snippet" style={{ color: 'var(--ink-500)', fontStyle: 'italic' }}>
                    Click to load body
                  </div>
                  <div className="mail-meta">
                    <span className={'chip ' + (m.status === 'Unread' ? 'primary' : m.status === 'Converted' ? 'ready' : 'muted')}>{m.status}</span>
                    {/* D2-UI: Link state chip */}
                    <span
                      style={{ cursor: (m.linkConfidence === 'Medium' || m.suggestedEnvelopeId) ? 'pointer' : 'default' }}
                      onClick={(e) => handleChipClick(e, m)}
                    >
                      <LinkChip email={m} />
                    </span>
                    <span className="mail-date">{new Date(m.receivedAt).toLocaleString()}</span>
                  </div>
                </div>
              ));
              return (
                <>
                  {rendered}
                  {page.hasMore && (
                    <div style={{ padding: 12, textAlign: 'center' }}>
                      <button className="btn btn-secondary btn-sm" disabled={loadingMore} onClick={loadMore}>
                        {loadingMore ? 'Loading…' : 'Load 100 more'}
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* Detail pane */}
        <div className="mailbox-view-col">
          {!selected ? (
            <div className="empty">
              <h3>Select an email</h3>
              <p>Convert customer emails into structured RFQs in one click, powered by Bedrock Nova.</p>
            </div>
          ) : (
            <article>
              <div className="mail-header">
                <div className="mail-header-row">
                  <b>{selected.fromName || selected.fromAddress}</b>
                  <span className="muted"> &lt;{selected.fromAddress}&gt;</span>
                </div>
                <h2>{selected.subject}</h2>
                <div className="muted">{new Date(selected.receivedAt).toLocaleString()} · {selected.source}</div>
                {/* D2-UI: Link state in detail pane */}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <LinkChip email={selected} />
                  {selected.linkedEnvelopeId && selected.linkConfidence === 'Manual' && (
                    <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                      Linked {selected.linkedAt ? new Date(selected.linkedAt).toLocaleDateString() : ''}
                    </span>
                  )}
                </div>
              </div>
              {selectedBody.loading ? (
                <div className="mail-body" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--ink-500)', fontStyle: 'italic' }}>
                  <span className="spinner" style={{ width: 12, height: 12, border: '2px solid var(--border-1)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                  Loading body from your mailbox…
                </div>
              ) : (
                <>
                  <pre className="mail-body">{selectedBody.body || selected.body || ''}</pre>
                  {selectedBody.source && selectedBody.source !== 'mongo' && selectedBody.source !== 'error' && (
                    <div style={{ fontSize: 11, color: 'var(--ink-500)', marginTop: -8 }}>
                      Body source: <span className="chip muted chip-sm">{selectedBody.source}</span>
                      {selectedBody.source === 'unavailable' && ' — the source mailbox no longer has this message and we did not cache it'}
                    </div>
                  )}
                </>
              )}

              {/* Action area — two tiers: primary "turn this email into an RFQ"
                  flow on top, secondary actions below. */}
              <div className="mail-actions" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
                {selected.status !== 'Converted' ? (
                  <>
                    {/* Primary: convert / create-customer flow */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-500)', marginRight: 4 }}>
                        Convert to RFQ
                      </span>
                      <ConvertControl customers={customers} selected={selected} onConvert={convert} onCreateCustomer={createCustomerOnly} busy={busy} />
                    </div>

                    {/* Secondary: link / thread / re-extract / archive */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', paddingTop: 8, borderTop: '1px solid var(--border-1)' }}>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setShowLinkPicker(true)}
                        data-testid="btn-link-rfq"
                      >
                        Link to RFQ + supplier
                      </button>
                      {selected.threadId && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => setActiveThreadId(selected.threadId)}
                          data-testid="btn-view-thread"
                        >
                          View thread
                        </button>
                      )}
                      {selected.linkedEnvelopeId && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={handleReextract}
                          disabled={busy}
                          data-testid="btn-reextract"
                        >
                          {busy ? 'Extracting…' : 'Re-extract'}
                        </button>
                      )}
                      <div style={{ flex: 1 }} />
                      <button className="btn btn-sm btn-ghost" onClick={archive} disabled={busy}>Archive</button>
                    </div>
                  </>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <div className="chip ready">
                      Converted {selected.convertedRfqId && <> → <a href={`/rfq/${selected.convertedRfqId}`}>open RFQ</a></>}
                    </div>
                    <div style={{ flex: 1 }} />
                    {selected.threadId && (
                      <button className="btn btn-sm btn-secondary" onClick={() => setActiveThreadId(selected.threadId)}>View thread</button>
                    )}
                    <button className="btn btn-sm btn-ghost" onClick={archive} disabled={busy}>Archive</button>
                  </div>
                )}
              </div>

              {/* D2-UI: Extracted preview banner if linked and preview exists */}
              {selected.linkedEnvelopeId && selected.extractedQuotePreview && (
                <div className="banner review" style={{ marginTop: 12 }}>
                  <span>🤖</span>
                  <div>
                    <b>Preview extracted</b> — {selected.extractedQuotePreview.lines?.length || 0} line(s) ready to commit in the Responses tab.
                  </div>
                </div>
              )}
            </article>
          )}
        </div>
      </div>

      {/* Modals */}
      {showLinkPicker && selected && (
        <LinkPickerModal
          email={selected}
          onClose={() => setShowLinkPicker(false)}
          onLinked={onLinked}
        />
      )}
      {showSuggestConfirm && selected && (
        <SuggestConfirmModal
          email={selected}
          onClose={() => setShowSuggestConfirm(false)}
          onConfirmed={onLinked}
        />
      )}

      {/* Thread drawer */}
      {activeThreadId && (
        <ThreadDrawer
          threadId={activeThreadId}
          onClose={() => setActiveThreadId(null)}
        />
      )}
    </Shell>
  );
}

function ConvertControl({ customers, selected, onConvert, onCreateCustomer, busy }) {
  // Ask the backend, on open, whether the sender domain matches an existing
  // customer and what name it would suggest creating. This replaces the old
  // client-side guess so the UI and the convert route agree on the match.
  const [suggestion, setSuggestion] = useState(null); // { name, domain, contactEmail }
  const [matched, setMatched] = useState(null); // { id, name } | null
  const [loadingSug, setLoadingSug] = useState(false);
  // Customer selected in the "pick existing" dropdown. Pre-filled to the
  // server-matched customer when there is one — never silently pick the first,
  // so we don't convert a Domino's receipt into an Airbus RFQ.
  const [customerId, setCustomerId] = useState('');
  // When there's no match we let the user pick existing instead of creating.
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingSug(true);
    setSuggestion(null);
    setMatched(null);
    setCustomerId('');
    setShowPicker(false);
    api.get(`/v1/inbound-emails/${selected._id}/customer-suggestion`)
      .then((r) => {
        if (cancelled) return;
        setSuggestion(r.suggestion || null);
        setMatched(r.matchedCustomer || null);
        if (r.matchedCustomer) setCustomerId(r.matchedCustomer.id);
      })
      .catch(() => { /* fall back to manual pick on error */ })
      .finally(() => { if (!cancelled) setLoadingSug(false); });
    return () => { cancelled = true; };
  }, [selected._id]);

  if (loadingSug) {
    return <span style={{ fontSize: 13, color: 'var(--ink-500)' }}>Checking customer…</span>;
  }

  // Case 1 — domain matched an existing customer: pre-select it, show which one.
  if (matched && !showPicker) {
    return (
      <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} data-testid="convert-matched">
        <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>
          Matched <b>{matched.name}</b>
        </span>
        <button
          className="btn btn-sm"
          onClick={() => onConvert({ customerId })}
          disabled={busy || !customerId}
          style={{ whiteSpace: 'nowrap' }}
        >
          {busy ? 'Converting…' : 'Convert to RFQ'}
        </button>
        <button
          className="btn btn-sm btn-ghost"
          onClick={() => setShowPicker(true)}
          disabled={busy}
          title="Convert against a different customer instead"
        >
          Change ▾
        </button>
      </div>
    );
  }

  // Case 2 — no match: offer one-click "Create <Name>" plus a pick-existing option.
  const sugName = suggestion?.name;
  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} data-testid="convert-no-match">
      {!matched && !showPicker && (
        <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>
          No customer matches <b>{suggestion?.domain || (selected.fromAddress || '').split('@')[1]}</b> —
        </span>
      )}
      {/* When the LLM enriched the suggestion (source:'llm'), show the contact
          details it pulled from the signature so the user can sanity-check
          before creating the customer. */}
      {suggestion?.source === 'llm' && (suggestion.contactName || suggestion.contactPhone) && (
        <span
          className="chip muted chip-sm"
          title="Extracted from the email signature by AI — verify before creating"
        >
          {[suggestion.contactName, suggestion.contactEmail, suggestion.contactPhone].filter(Boolean).join(' · ')}
        </span>
      )}
      {sugName && (
        <button
          className="btn btn-sm"
          onClick={async () => {
            // Create the customer ONLY. On success, select it in the dropdown so
            // the user reviews, then clicks Convert as a separate, deliberate step.
            const newId = await onCreateCustomer({ ...suggestion, defaultCurrency: 'USD' });
            if (newId) {
              setCustomerId(newId);
              setShowPicker(true);
            }
          }}
          disabled={busy}
          style={{ whiteSpace: 'nowrap' }}
          data-testid="btn-create-customer"
          title={`Create customer "${sugName}" — you'll convert as a separate step`}
        >
          {busy ? 'Working…' : `Create "${sugName}"`}
        </button>
      )}
      <select
        className="select select-sm"
        value={customerId}
        onChange={(e) => setCustomerId(e.target.value)}
        style={{ maxWidth: 220, height: 32, padding: '4px 8px', fontSize: 13 }}
      >
        <option value="">— Pick existing —</option>
        {customers.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
      </select>
      <button
        className="btn btn-sm btn-secondary"
        onClick={() => onConvert({ customerId })}
        disabled={busy || !customerId}
        style={{ whiteSpace: 'nowrap' }}
        title={!customerId ? 'Pick a customer first' : ''}
      >
        {busy ? 'Converting…' : 'Convert to RFQ'}
      </button>
    </div>
  );
}

/**
 * Mailbox search bar.
 * Modes:
 *  - Local filter — searches the currently-loaded `data.items` by subject/from.
 *  - "Search provider" — calls /v1/mailbox/connectors/:id/search with the query
 *    to ingest older / already-read messages the unread-only poll misses.
 *    Gmail uses Gmail query syntax; Microsoft uses Graph $search.
 *  - "Sync recent" (Microsoft) — pulls the most recent Inbox mail (read + unread)
 *    with no query, so an RFQ you already opened in Outlook still gets ingested.
 */
function MailboxSearchBar({ connectors, selectedConnectorId, localFilter, setLocalFilter, onAfterRemoteSearch }) {
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const toast = useToast();
  // Prefer the connector currently selected in the rail; otherwise the first
  // connected searchable provider (Gmail or Microsoft).
  const selectedConnector = connectors.find((c) => String(c._id) === String(selectedConnectorId));
  const searchable = (c) => c && c.status === 'connected' && (c.provider === 'gmail' || c.provider === 'microsoft');
  const conn = searchable(selectedConnector)
    ? selectedConnector
    : connectors.find(searchable);
  const providerName = conn?.provider === 'microsoft' ? 'Outlook' : conn?.provider === 'gmail' ? 'Gmail' : '';

  async function searchProvider() {
    if (!conn) {
      toast.push('Connect Gmail or Outlook first to pull older messages', { variant: 'error' });
      return;
    }
    if (!localFilter.trim()) return;
    setBusy(true);
    try {
      const r = await api.searchConnector(conn._id, { q: localFilter, maxResults: 30 });
      toast.push(`Pulled ${r.ingested} of ${r.scanned} matching message(s) into Mailbox`);
      onAfterRemoteSearch && onAfterRemoteSearch();
    } catch (e) {
      toast.push(`${providerName} search failed: ` + e.message, { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  // Microsoft-only: pull recent Inbox mail (read + unread) without a query.
  async function syncRecent() {
    if (!conn) return;
    setSyncing(true);
    try {
      const r = await api.searchConnector(conn._id, { maxResults: 40 });
      toast.push(`Synced ${r.ingested} recent message(s) from ${providerName}`);
      onAfterRemoteSearch && onAfterRemoteSearch();
    } catch (e) {
      toast.push(`${providerName} sync failed: ` + e.message, { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
      <input
        type="search"
        className="input"
        placeholder={conn ? `Filter or search ${providerName}…` : 'Filter loaded emails…'}
        value={localFilter}
        onChange={(e) => setLocalFilter(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && conn) searchProvider(); }}
        style={{ flex: 1, height: 32, padding: '4px 8px', fontSize: 13 }}
      />
      {conn && (
        <button
          className="btn btn-sm btn-secondary"
          onClick={searchProvider}
          disabled={busy || !localFilter.trim()}
          title={`Search ${conn.displayEmail} for older or already-read messages matching this query`}
          style={{ whiteSpace: 'nowrap' }}
        >
          {busy ? 'Searching…' : `🔎 Search ${providerName}`}
        </button>
      )}
      {conn?.provider === 'microsoft' && (
        <button
          className="btn btn-sm btn-secondary"
          onClick={syncRecent}
          disabled={syncing}
          title={`Pull recent mail (read + unread) from ${conn.displayEmail} — the regular poll only fetches unread`}
          style={{ whiteSpace: 'nowrap' }}
        >
          {syncing ? 'Syncing…' : '↻ Sync recent'}
        </button>
      )}
      {localFilter && (
        <button className="btn btn-sm btn-ghost" onClick={() => setLocalFilter('')} title="Clear">×</button>
      )}
    </div>
  );
}
